import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filter, propEq } from 'ramda';
import moment from 'moment';

import { Container, Grid, Typography } from '@material-ui/core';
import MTable from '../../../components/MTable';
import TableMobile from '../../../components/TableMobile';
import Button from '../../../components/Button';
import Resume from './Resume';
import PeriodType from './PeriodType';
import BuildResumeModal from './BuildResumeModal';

import { KeyboardArrowDown } from '@material-ui/icons';

import { loadCashFlowResume, loadCashFlow, loadCashFlowByDay, loadCashFlowByMonth } from '../store/thunk';
import { setYearRange, handleBuildResume, clearCashFlow, clearResume, clearBuildResumeFilters } from '../store/actions';

import { useStyles } from './styles';

import useDidMount from 'helpers/hooks/useDidMount';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

const IconResume = require('../../../assets/img/icon-resume.svg');

let headers = [];

const dayHeaders = [
  { title: 'Data', field: 'day', sorting: false },
  { title: 'Recebimentos', field: 'amountReceipt', sorting: false },
  { title: 'Pagamentos', field: 'amountPayment', sorting: false }
];

const monthHeaders = [
  {
    title: 'Mês',
    field: 'month',
    sorting: true,
    customSort: (a, b) => a.monthId - b.monthId
  },
  {
    title: 'Total de recebimentos',
    field: 'amountReceipt',
    sorting: false
  },
  { title: 'Total de pagamentos', field: 'amountPayment', sorting: false },
  { title: 'Lucro', field: 'amountBalance', sorting: false }
];

headers = dayHeaders;

export default function CashFlow() {
  const dispatch = useDispatch();

  const classes = useStyles();

  const didMount = useDidMount();

  const { width } = useWindowDimensions();
  const isMobile = width < 960;

  const { userId } = useSelector(state => state.profile);
  const establishmentId = useSelector(state => state.businessInfo.establishmentId);
  const establishments = useSelector(state => state.login.establishments);

  const {
    type,
    month,
    year,
    buildingResume,
    content,
    singleEstablishmentContent,
    isLoading,
    isResumeLoading
  } = useSelector(state => state.cashFlow);

  const [hasLoadedPreviousDates, setHasLoadedPreviousDates] = useState(false);
  const [hasLoadedNextDates, setHasLoadedNextDates] = useState(false);
  const [hasLoadedAllDates, setHasLoadedAllDates] = useState(false);

  const [loadedYears, setLoadedYears] = useState([]);
  const [loadedPreviousYears, setLoadedPreviousYears] = useState([]);
  const [loadedMonths, setLoadedMonths] = useState([]);
  const [loadedPreviousMonths, setLoadedPreviousMonths] = useState([]);

  const [currentLoadingMonths, setCurrentLoadingMonths] = useState([]);
  const [currentLoadingYears, setCurrentLoadingYears] = useState([]);
  const [currentLoadingEstablishments, setCurrentLoadingEstablishments] = useState([]);

  const [isAllEstablishmentResumeLoaded, setIsAllEstablishmentResumeLoaded] = useState(false);
  const [isDateSelected, setIsDateSelected] = useState(true);

  const mountHeader = type => {
    headers = type == 'day' ? dayHeaders : monthHeaders;
  }

  const filterEstablishmentForSetYearRange = () => {
    const getElementById = filter(propEq('establishmentId', establishmentId));
    const establishmentFiltered = getElementById(establishments);

    const baseYear = parseInt(
      establishmentFiltered[0]?.created_at
        ? moment(establishmentFiltered[0]?.created_at).format('YYYY')
        : moment().format('YYYY'),
      10
    );

    const finalYear = baseYear + 50;

    setYearRange([baseYear, finalYear]);
  }

  useEffect(() => {
    if(didMount) {
      dispatch(clearCashFlow());
    }
  }, [didMount]);

  useEffect(() => {
    if(currentLoadingEstablishments.includes(establishmentId)) {
      return;
    }

    setCurrentLoadingEstablishments([...new Set([...currentLoadingEstablishments, establishmentId])]);

    setCurrentLoadingYears([]);
    setCurrentLoadingMonths([]);

    setHasLoadedPreviousDates(false);
    setHasLoadedNextDates(false);
    setHasLoadedAllDates(false);
  }, [establishmentId]);

  useEffect(() => {
    dispatch(clearResume());

    setHasLoadedPreviousDates(false);
    setHasLoadedNextDates(false);
    setHasLoadedAllDates(false);
  }, [establishmentId, type, year, month]);

  useEffect(() => {
    setIsDateSelected(false);
  }, [month, year]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsDateSelected(true);
    }, 1500);

    return () => clearTimeout(timeout);
  }, [month, year]);

  useEffect(() => {
    setIsAllEstablishmentResumeLoaded(false);
  }, [type]);

  useEffect(() => {
    if(!establishmentId) {
      if(type == 'day') {
        const startDate = `${year}-${month}-01`;
        const endDate = moment(`${year}-${month}`, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');

        dispatch(loadCashFlowResume(userId, startDate, endDate))
          .then(() => setIsAllEstablishmentResumeLoaded(true));
      }

      if(type == 'month') {
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;

        dispatch(loadCashFlowResume(userId, startDate, endDate))
          .then(() => setIsAllEstablishmentResumeLoaded(true));
      }
    }
  }, [establishmentId, type, year, month]);

  const isPastMonth = moment(moment.now()).diff(moment(`${year}-${month}`, 'YYYY-MM'), 'month') > 0;
  const isFutureMonth = moment(`${year}-${month}`, 'YYYY-MM').isAfter(moment.now());

  const days = Array
    .from({ length: isPastMonth ? moment(moment(`${year}-${month}-01`, 'YYYY-MM-DD')).endOf('month').format('D') : moment(moment.now()).format('D') })
    .map((_, index) => `${year}-${(month).toString().padStart(2, '0')}-${(index + 1).toString().padStart(2, '0')}`);

  const requestDay = (day, limit) => {
    if(!day || +limit == 0) {
      setLoadedPreviousMonths(loadedPreviousMonths.filter(previousMonth => previousMonth != `${year}-${month}`));
      setHasLoadedPreviousDates(true);
      return;
    }

    const currentDay = days.indexOf(day);

    if(+currentDay == 0) {
      setHasLoadedAllDates(true);
    }

    dispatch(loadCashFlowByDay(userId, establishmentId, day))
      .finally(() => requestDay(days[currentDay - 1], limit - 1));
  }

  const currentYear = moment(moment.now()).year();
  const currentMonth = moment(moment.now()).month() + 1;
  const isPastYear = year < currentYear;
  const isFutureYear = year > currentYear;

  const months = Array
    .from({ length: isPastYear ? 12 : currentMonth })
    .map((_, index) => `${year}-${(index + 1).toString().padStart(2, '0')}`);

  const requestMonth = (month, limit) => {
    if(!month || +limit == 0) {
      setLoadedPreviousYears(loadedPreviousYears.filter(previousYear => previousYear != year));
      setHasLoadedPreviousDates(true);
      return;
    }

    const currentMonth = months.indexOf(month);

    if(+currentMonth == 0) {
      setHasLoadedAllDates(true);
    }

    dispatch(loadCashFlowByMonth(userId, establishmentId, month))
      .finally(() => requestMonth(months[currentMonth - 1], limit - 1));
  }

  useEffect(() => {
    if(establishmentId) {
      return;
    }

    if(!isAllEstablishmentResumeLoaded) {
      return;
    }

    if(!isDateSelected) {
      return;
    }

    if(type == 'day') {
      if(isFutureMonth || hasLoadedAllDates) {
        return;
      }

      if(!hasLoadedPreviousDates) {
        const monthAndYear = moment(days[days.length - 1], 'YYYY-MM-DD').format('YYYY-MM');

        if(currentLoadingMonths.includes(monthAndYear)) {
          return;
        }

        setCurrentLoadingMonths([...new Set([...currentLoadingMonths, monthAndYear])]);
        setLoadedPreviousMonths([...new Set([...loadedPreviousMonths, monthAndYear])]);

        requestDay(days[days.length - 1], 10);
      }
    }

    if(type == 'month') {
      if((year > currentYear) || hasLoadedAllDates) {
        return;
      }

      if(!hasLoadedPreviousDates) {
        const year = moment(months[months.length - 1], 'YYYY-MM').year();

        if(currentLoadingYears.includes(year)) {
          return;
        }

        setCurrentLoadingYears([...new Set([...currentLoadingYears, year])]);
        setLoadedPreviousYears([...new Set([...loadedPreviousYears, year])]);

        requestMonth(months[months.length - 1], 6);
      }
    }
  }, [establishmentId, year, month, type, hasLoadedPreviousDates, isDateSelected, isAllEstablishmentResumeLoaded, hasLoadedNextDates, hasLoadedAllDates, currentLoadingMonths, window, document]);

  useEffect(() => {
    if(!establishmentId) {
      return;
    }

    dispatch(loadCashFlow(userId, establishmentId, type, month, year));
  }, [establishmentId, type, month, year]);

  useEffect(() => {
    if(establishmentId || type || year || month) {
      mountHeader(type);

      if(establishmentId) {
        filterEstablishmentForSetYearRange();
      }
    }
  }, [establishmentId, type, year, month]);

  const onBuildResume = () => {
    dispatch(clearBuildResumeFilters());
    dispatch(handleBuildResume(true));
  }

  return(
    <>
      {buildingResume && (
        <BuildResumeModal
          isOpen={buildingResume}
          userId={userId}
          establishmentId={establishmentId}
        />
      )}
      {isMobile && (
        <Grid item xs={12}>
          <Button
            fullWidth
            type="button"
            color="success"
            onClick={onBuildResume}
            startIcon={
              <img
                src={IconResume}
                width={20}
                height={20}
              />
            }
          >
            Gerar Resumo
          </Button>
        </Grid>
      )}
      <Resume isLoading={isResumeLoading} />
      <Container className={classes.container} style={{ marginBottom: isMobile ? 10 : 0 }}>
        <Grid container>
          {!isMobile && (
            <Grid item xs={12} md={4}>
              <Button
                type="button"
                color="success"
                onClick={onBuildResume}
              >
                Gerar Resumo
              </Button>
            </Grid>
          )}
          <Grid item xs={12} md={8}>
            <Grid container justify="flex-end">
              <Grid item>
                {!isMobile && (
                  <PeriodType active={type} />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <div id="cashflow-table">
        <>
          {establishmentId && (
            <>
              {isMobile && (
                <TableMobile
                  loading={isLoading}
                  data={singleEstablishmentContent?.map(item => {
                    return {
                      ...item,
                      day: {
                        index: 1,
                        label: 'Data',
                        value: <Typography color='textSecondary' variant='subtitle2'>{item?.day}</Typography>
                      },
                      amountReceipt: {
                        index: 2,
                        label: 'Recebimentos',
                        value: <Typography color='textSecondary' variant='subtitle2'>{item?.amountReceipt}</Typography>
                      },
                      amountPayment: {
                        index: 3,
                        label: 'Pagamentos',
                        value: <Typography color='textSecondary' variant='subtitle2'>{item?.amountPayment}</Typography>
                      }
                    }
                  })}
                />
              )}
              {!isMobile && (
                <MTable
                  hasSorting
                  loading={isLoading}
                  headers={headers}
                  data={singleEstablishmentContent}
                />
              )}
            </>
          )}
          {!establishmentId && (
            <>
              {isMobile && (
                <TableMobile
                  loading={isLoading}
                  data={[...(type == 'month')
                    ? content
                        ?.filter(item => +item?.establishmentId == +establishmentId)
                        ?.filter(item => item?.isMonth)
                        ?.filter(item => moment(item?.day, 'DD/MM/YYYY').year() == +year)
                        ?.sort((a, b) => a?.monthId + b?.monthId)
                    : content
                        ?.filter(item => +item?.establishmentId == +establishmentId)
                        ?.filter(item => item?.isDay)
                        ?.filter(item => moment(item?.day, 'DD/MM/YYYY').month() + 1 == +month)
                        ?.filter(item => moment(item?.day, 'DD/MM/YYYY').year() == +year)
                        ?.sort((a, b) => a?.dayId + b?.dayId)]?.map(item => {
                          return {
                            ...item,
                            day: {
                              index: 1,
                              label: 'Data',
                              value: <Typography color='textSecondary' variant='subtitle2'>{type == 'month' ? item?.month : item?.day}</Typography>
                            },
                            amountReceipt: {
                              index: 2,
                              label: 'Recebimentos',
                              value: <Typography color='textSecondary' variant='subtitle2'>{item?.amountReceipt}</Typography>
                            },
                            amountPayment: {
                              index: 3,
                              label: 'Pagamentos',
                              value: <Typography color='textSecondary' variant='subtitle2'>{item?.amountPayment}</Typography>
                            }
                          }
                  })}
                />
              )}
              {!isMobile && (
                <MTable
                  hasSorting
                  loading={isLoading}
                  headers={headers}
                  data={(type == 'month')
                    ? content
                        ?.filter(item => +item?.establishmentId == +establishmentId)
                        ?.filter(item => item?.isMonth)
                        ?.filter(item => moment(item?.day, 'DD/MM/YYYY').year() == +year)
                        ?.sort((a, b) => a?.monthId + b?.monthId)
                    : content
                        ?.filter(item => +item?.establishmentId == +establishmentId)
                        ?.filter(item => item?.isDay)
                        ?.filter(item => moment(item?.day, 'DD/MM/YYYY').month() + 1 == +month)
                        ?.filter(item => moment(item?.day, 'DD/MM/YYYY').year() == +year)
                        ?.sort((a, b) => a?.dayId + b?.dayId)
                  }
                />
              )}
              {(type == 'day' && !isResumeLoading && !isFutureMonth && !loadedPreviousMonths.includes(`${year}-${month}`) && !loadedMonths.includes(`${year}-${month}`) && days.length > 10) && (
                <div
                  id='cashflow-load-more-div'
                  style={{ width: '100%', height: 50, backgroundColor: '#022A5C', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => {
                    setLoadedMonths([...new Set([...loadedMonths, `${year}-${month}`])]);
                    requestDay(days[days.length - 11]);
                  }}
                >
                  <KeyboardArrowDown style={{ color: '#FFFFFF', fontSize: 40 }} />
                </div>
              )}
              {(type == 'month' && !isResumeLoading && !isFutureYear && !loadedPreviousYears.includes(year) && !loadedYears.includes(year) && months.length > 6) && (
                <div
                  id='cashflow-load-more-div'
                  style={{ width: '100%', height: 50, backgroundColor: '#022A5C', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => {
                    setLoadedYears([...new Set([...loadedYears, year])]);
                    requestMonth(months[months.length - 7]);
                  }}
                >
                  <KeyboardArrowDown style={{ color: '#FFFFFF', fontSize: 30 }} />
                </div>
              )}
            </>
          )}
        </>
      </div>
    </>
  );
}